import { useState } from "react";
import { useServerFn } from "@tanstack/react-start";
import { submitFeedback } from "@/lib/journey.functions";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { MessageCircle } from "lucide-react";
import { toast } from "sonner";

export function FeedbackDialog({ trigger }: { trigger?: React.ReactNode }) {
  const [open, setOpen] = useState(false);
  const [kind, setKind] = useState("suggestion");
  const [subject, setSubject] = useState("");
  const [message, setMessage] = useState("");
  const [busy, setBusy] = useState(false);
  const send = useServerFn(submitFeedback);

  const submit = async () => {
    if (!subject.trim() || !message.trim()) return;
    setBusy(true);
    try {
      await send({ data: { kind, subject, message, page_path: typeof window !== "undefined" ? window.location.pathname : undefined } });
      toast.success("Thank you — received.");
      setOpen(false); setSubject(""); setMessage("");
    } catch (e) { toast.error((e as Error).message); }
    finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        {trigger ?? (
          <Button variant="ghost" size="sm" className="text-foreground/70">
            <MessageCircle size={14} className="mr-1" />Feedback
          </Button>
        )}
      </DialogTrigger>
      <DialogContent>
        <DialogHeader><DialogTitle>Send feedback</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <select
            aria-label="Feedback type"
            value={kind}
            onChange={(e) => setKind(e.target.value)}
            className="w-full rounded-md border hairline bg-obsidian px-2 py-2 text-sm"
          >
            <option value="suggestion">Suggestion</option>
            <option value="bug">Bug / broken link</option>
            <option value="accessibility">Accessibility issue</option>
            <option value="content">Content suggestion</option>
            <option value="other">Other</option>
          </select>
          <Input placeholder="Subject" value={subject} onChange={(e) => setSubject(e.target.value)} maxLength={200} />
          <Textarea placeholder="What would you like to share?" value={message} onChange={(e) => setMessage(e.target.value)} maxLength={4000} className="min-h-32" />
          <Button onClick={submit} disabled={busy || !subject.trim() || !message.trim()}>Send</Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
